/**
 * The report catalog, the custom-report builder's metadata, a deterministic
 * preview result and the exports history.
 */

import type {
  BusinessArea,
  DownloadedReportItem,
  RecentlyViewedReport,
  RecordType,
  RecordTypeField,
  ReportDefinitionSummary,
  ReportListItem,
  ReportPreviewData,
  ReportScreenFilter,
} from '@/services/tenant/custom-report-services';

import { TOUR_AWARDS } from './tour-sample-awards';

export const TOUR_REPORTS: readonly ReportListItem[] = Object.freeze([
  {
    id: 3001,
    code: 'RPT-AWARD-SUMMARY',
    name: 'Award Summary',
    description: 'Outstanding awards by plan, status and grant year.',
    isStandard: true,
    isShared: false,
    businessAreaId: 1,
  },
  {
    id: 3002,
    code: 'RPT-AWARD-DETAIL',
    name: 'Award Detail',
    description: 'Every award with its vesting schedule and current status.',
    isStandard: true,
    isShared: false,
    businessAreaId: 1,
  },
  {
    id: 3003,
    code: 'RPT-VESTING-FORECAST',
    name: 'Vesting Forecast',
    description: 'Shares expected to vest over the next 24 months.',
    isStandard: true,
    isShared: true,
    businessAreaId: 1,
  },
  {
    id: 3004,
    code: 'RPT-EXPENSE',
    name: 'Share-Based Expense',
    description: 'IFRS 2 expense accrual by period and cost center.',
    isStandard: true,
    isShared: false,
    businessAreaId: 2,
  },
  {
    id: 3005,
    code: 'RPT-SHARE-DILUTION',
    name: 'Share Dilution',
    description: 'Dilution against issued capital by plan.',
    isStandard: true,
    isShared: true,
    businessAreaId: 2,
  },
  {
    id: 3006,
    code: 'RPT-KEY-MGMT',
    name: 'Key Management Holdings',
    description: 'Holdings and movements for key management personnel.',
    isStandard: true,
    isShared: false,
    businessAreaId: 3,
  },
  {
    id: 3007,
    code: 'RPT-AUDIT-LOG',
    name: 'Audit Log',
    description: 'Maker-checker activity across the tenant.',
    isStandard: true,
    isShared: false,
    businessAreaId: 4,
  },
  {
    id: 3008,
    code: 'RPT-FINANCE-QTR',
    name: 'Quarterly Finance Pack',
    description: 'Custom report built by Finance for the quarterly board pack.',
    isStandard: false,
    isShared: true,
    isMine: true,
    businessAreaId: 2,
  },
]);

export const TOUR_RECENTLY_VIEWED_REPORTS: readonly RecentlyViewedReport[] =
  Object.freeze([
    { id: 3001, code: 'RPT-AWARD-SUMMARY', name: 'Award Summary' },
    { id: 3004, code: 'RPT-EXPENSE', name: 'Share-Based Expense' },
    { id: 3003, code: 'RPT-VESTING-FORECAST', name: 'Vesting Forecast' },
  ]);

export const TOUR_BUSINESS_AREAS: readonly BusinessArea[] = Object.freeze([
  {
    id: 1,
    code: 'AWARDS',
    name: 'Awards',
    description: 'Grants, vesting and participant responses.',
    maxColumns: 25,
    maxFilters: 10,
    maxGroupings: 5,
    maxRecordTypes: 3,
  },
  {
    id: 2,
    code: 'FINANCE',
    name: 'Finance',
    description: 'Expense, dilution and treasury movements.',
    maxColumns: 25,
    maxFilters: 10,
    maxGroupings: 5,
    maxRecordTypes: 3,
  },
  {
    id: 3,
    code: 'PARTICIPANTS',
    name: 'Participants',
    description: 'Employee and holdings data.',
    maxColumns: 20,
    maxFilters: 8,
    maxGroupings: 4,
    maxRecordTypes: 2,
  },
  {
    id: 4,
    code: 'GOVERNANCE',
    name: 'Governance',
    description: 'Approvals, cases and audit activity.',
    maxColumns: 20,
    maxFilters: 8,
    maxGroupings: 4,
    maxRecordTypes: 2,
  },
]);

export const TOUR_RECORD_TYPES: Record<number, RecordType[]> = {
  1: [
    {
      id: 101,
      code: 'AWARD',
      name: 'Award',
      isAnchor: true,
      combinableWith: [102, 103],
    },
    {
      id: 102,
      code: 'VESTING',
      name: 'Vesting Tranche',
      isAnchor: false,
      combinableWith: [101],
    },
    {
      id: 103,
      code: 'PARTICIPANT',
      name: 'Participant',
      isAnchor: false,
      combinableWith: [101],
    },
  ],
  2: [
    {
      id: 201,
      code: 'EXPENSE',
      name: 'Expense Accrual',
      isAnchor: true,
      combinableWith: [202],
    },
    {
      id: 202,
      code: 'SECURITY',
      name: 'Security',
      isAnchor: false,
      combinableWith: [201],
    },
  ],
  3: [
    {
      id: 301,
      code: 'EMPLOYEE',
      name: 'Employee',
      isAnchor: true,
      combinableWith: [302],
    },
    {
      id: 302,
      code: 'HOLDING',
      name: 'Holding',
      isAnchor: false,
      combinableWith: [301],
    },
  ],
  4: [
    {
      id: 401,
      code: 'TXN',
      name: 'Approval Transaction',
      isAnchor: true,
      combinableWith: [],
    },
  ],
};

const textField = (
  id: number,
  column: string,
  label: string,
  dataType = 'String'
): RecordTypeField => ({
  id,
  tableColumnName: column,
  displayName: label,
  dataType,
  displayFormat: dataType === 'Date' ? 'dd MMM yyyy' : null,
  isSelectable: true,
  isFilterable: true,
  isSortable: true,
  isGroupable: dataType === 'String',
  allowedOperators:
    dataType === 'Number'
      ? ['eq', 'gt', 'lt', 'between']
      : ['eq', 'contains', 'in'],
  allowedAggregations:
    dataType === 'Number' ? ['sum', 'avg', 'max', 'min'] : [],
});

export const TOUR_RECORD_TYPE_FIELDS: Record<number, RecordTypeField[]> = {
  101: [
    textField(1001, 'awardNo', 'Award No'),
    textField(1002, 'planName', 'Plan Name'),
    textField(1003, 'grantDate', 'Grant Date', 'Date'),
    textField(1004, 'awardedShares', 'Awarded Shares', 'Number'),
    textField(1005, 'statusName', 'Status'),
  ],
  102: [
    textField(1011, 'vestingDate', 'Vesting Date', 'Date'),
    textField(1012, 'sharesVested', 'Shares Vested', 'Number'),
    textField(1013, 'vestingPercentage', 'Vesting %', 'Number'),
  ],
  103: [
    textField(1021, 'employeeId', 'Employee ID'),
    textField(1022, 'employeeName', 'Employee Name'),
    textField(1023, 'department', 'Department'),
  ],
  201: [
    textField(1031, 'period', 'Period'),
    textField(1032, 'expenseAmount', 'Expense Amount', 'Number'),
    textField(1033, 'costCenter', 'Cost Center'),
  ],
  202: [
    textField(1041, 'securityName', 'Security'),
    textField(1042, 'exchange', 'Exchange'),
  ],
  301: [
    textField(1051, 'employeeId', 'Employee ID'),
    textField(1052, 'fullName', 'Employee Name'),
    textField(1053, 'department', 'Department'),
  ],
  302: [
    textField(1061, 'holdingShares', 'Shares Held', 'Number'),
    textField(1062, 'securityName', 'Security'),
  ],
  401: [
    textField(1071, 'txnId', 'Transaction ID'),
    textField(1072, 'moduleName', 'Module'),
    textField(1073, 'txnStatus', 'Status'),
  ],
};

export const TOUR_REPORT_DEFINITIONS: readonly ReportDefinitionSummary[] =
  Object.freeze([
    {
      id: 5501,
      code: 'RPT-FINANCE-QTR',
      name: 'Quarterly Finance Pack',
      description: 'Expense and dilution for the quarterly board pack.',
      businessAreaId: 2,
      isShared: true,
      createdAt: '2025-01-09T09:30:00Z',
      updatedAt: '2025-07-02T14:10:00Z',
    },
    {
      id: 5502,
      code: 'RPT-VEST-NEXT-Q',
      name: 'Vesting Next Quarter',
      description: 'Awards vesting inside the next three months.',
      businessAreaId: 1,
      isShared: false,
      createdAt: '2025-04-21T10:05:00Z',
      updatedAt: null,
    },
  ]);

/** The detail behind a saved custom report definition. */
export const getTourReportDefinition = (id: number | string) => {
  const summary =
    TOUR_REPORT_DEFINITIONS.find(item => String(item.id) === String(id)) ??
    TOUR_REPORT_DEFINITIONS[0];
  return {
    code: summary.code,
    name: summary.name,
    description: summary.description,
    businessAreaId: summary.businessAreaId,
    isShared: summary.isShared,
    allowedFormats: ['pdf', 'excel', 'csv'],
    defaultPageSize: 25,
    maxPageSize: 200,
    applyRowLevelFilter: true,
    fields: [
      {
        recordTypeId: 101,
        fieldId: 1001,
        aggregation: null,
        isFilter: false,
        filterOperator: null,
        filterValue: null,
        isSort: true,
        sortDescending: false,
      },
      {
        recordTypeId: 101,
        fieldId: 1002,
        aggregation: null,
        isFilter: false,
        filterOperator: null,
        filterValue: null,
        isSort: false,
        sortDescending: false,
      },
      {
        recordTypeId: 101,
        fieldId: 1004,
        aggregation: 'sum',
        isFilter: false,
        filterOperator: null,
        filterValue: null,
        isSort: false,
        sortDescending: false,
      },
      {
        recordTypeId: 103,
        fieldId: 1022,
        aggregation: null,
        isFilter: false,
        filterOperator: null,
        filterValue: null,
        isSort: false,
        sortDescending: false,
      },
    ],
  };
};

/** Filter controls offered on a report's run screen. */
export const TOUR_REPORT_FILTERS: readonly ReportScreenFilter[] = Object.freeze(
  [
    {
      key: 'planType',
      label: 'Plan Type',
      dataType: 'String',
      control: 'multiselect',
      operator: 'in',
      required: false,
      options: [
        { value: 'PSP', label: 'Performance Share Plan' },
        { value: 'RSP', label: 'Restricted Share Plan' },
        { value: 'ESOS', label: 'Employee Share Option Scheme' },
      ],
    },
    {
      key: 'status',
      label: 'Award Status',
      dataType: 'String',
      control: 'multiselect',
      operator: 'in',
      required: false,
      options: [
        { value: 'Accepted', label: 'Accepted' },
        { value: 'Action Required', label: 'Action Required' },
        { value: 'Vested', label: 'Vested' },
        { value: 'Lapsed', label: 'Lapsed' },
      ],
    },
    {
      key: 'grantDate',
      label: 'Grant Date',
      dataType: 'Date',
      control: 'dateRange',
      operator: 'between',
      required: false,
      options: null,
    },
  ]
);

/**
 * A deterministic report result, built from the shared award fixtures.
 *
 * This is the **custom report builder's** preview shape
 * (`previewReportDefinition`): `columns` + `rows` + `rowCount`. The report
 * preview screen parses a different shape — see `getTourReportResult`.
 */
export const TOUR_REPORT_PREVIEW: ReportPreviewData = {
  columns: [
    { key: 'c0', label: 'Award No', dataType: 'String', displayFormat: null },
    {
      key: 'c1',
      label: 'Employee Name',
      dataType: 'String',
      displayFormat: null,
    },
    { key: 'c2', label: 'Plan Name', dataType: 'String', displayFormat: null },
    {
      key: 'c3',
      label: 'Grant Date',
      dataType: 'Date',
      displayFormat: 'dd MMM yyyy',
    },
    {
      key: 'c4',
      label: 'Awarded Shares',
      dataType: 'Number',
      displayFormat: '#,##0',
    },
    { key: 'c5', label: 'Status', dataType: 'String', displayFormat: null },
  ],
  rows: TOUR_AWARDS.map(award => [
    award.awardNo,
    award.employeeName,
    award.planName,
    award.awardDate,
    award.awardedShares,
    award.statusName,
  ]),
  rowCount: TOUR_AWARDS.length,
  truncated: false,
};

/**
 * The same result in the shape the report preview screen reads.
 *
 * `parseReportResponse` (ReportDetailView) only recognises a payload with
 * `columns` **and `items`**, and reads the total from `totalCount` — handed
 * `rows`/`rowCount` it falls through to its empty branch and the screen shows
 * "No records found" after a successful Generate.
 *
 * `offset` here is a ROW offset (page N of size S starts at N*S), which is how
 * the data route differs from the paged list routes, so the slice mirrors
 * that rather than going through `paginateTourData`.
 */
export const getTourReportResult = (params: {
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: string;
}) => {
  const { columns, rows } = TOUR_REPORT_PREVIEW;
  const sortIndex = params.sortBy
    ? columns.findIndex(column => column.key === params.sortBy)
    : -1;

  const toCellString = (val: unknown): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'number' || typeof val === 'boolean') return String(val);
    return JSON.stringify(val);
  };

  const ordered =
    sortIndex === -1
      ? [...rows]
      : [...rows].sort((left, right) => {
          const a = left[sortIndex];
          const b = right[sortIndex];
          const compared =
            typeof a === 'number' && typeof b === 'number'
              ? a - b
              : toCellString(a).localeCompare(toCellString(b), undefined, {
                  sensitivity: 'base',
                });
          return String(params.sortDir).toLowerCase() === 'desc'
            ? -compared
            : compared;
        });

  const start = Math.max(0, Number(params.offset) || 0);
  const limit =
    Number(params.limit) > 0 ? Number(params.limit) : ordered.length;

  return {
    columns,
    items: ordered.slice(start, start + limit),
    totalCount: ordered.length,
    truncated: false,
  };
};

export const TOUR_DOWNLOADED_REPORTS: readonly DownloadedReportItem[] =
  Object.freeze([
    {
      id: 'tour-run-0001',
      reportCode: 'RPT-AWARD-SUMMARY',
      reportName: 'Award Summary',
      initiatedByName: 'Arun Kumar',
      initiatedAt: '2025-09-18T09:14:00Z',
      format: 'Excel',
      status: 'Downloaded',
      reportData: [
        { field: 'planType', op: 'in', value: 'PSP', label: 'Plan Type - PSP' },
      ],
    },
    {
      id: 'tour-run-0002',
      reportCode: 'RPT-EXPENSE',
      reportName: 'Share-Based Expense',
      initiatedByName: 'Sarah Lim',
      initiatedAt: '2025-09-15T16:42:00Z',
      format: 'Pdf',
      status: 'Email Sent',
      reportData: [],
    },
    {
      id: 'tour-run-0003',
      reportCode: 'RPT-VESTING-FORECAST',
      reportName: 'Vesting Forecast',
      initiatedByName: 'Arun Kumar',
      initiatedAt: '2025-09-11T11:02:00Z',
      format: 'Csv',
      status: 'Downloaded',
      reportData: [
        {
          field: 'status',
          op: 'in',
          value: 'Accepted',
          label: 'Status - Accepted',
        },
      ],
    },
  ]);

/** Catalog counts, derived so they always agree with the list above. */
export const getTourReportCounts = () => ({
  total: TOUR_REPORTS.length,
  standard: TOUR_REPORTS.filter(report => report.isStandard).length,
  shared: TOUR_REPORTS.filter(report => report.isShared).length,
  my: TOUR_REPORTS.filter(report => report.isMine).length,
  custom: TOUR_REPORTS.filter(report => !report.isStandard).length,
});
